import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  FileText,
  Plus,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Calendar,
  Send,
  ChevronsUpDown,
  Check,
  ArrowRight,
  Filter,
  User,
  RotateCcw,
  Snowflake,
  Trash2,
  Download,
  Printer,
} from "lucide-react";
import { toast } from "sonner";
import { searchICD10 } from "@/data/icd10Codes";
import { cn } from "@/lib/utils";
import {
  DenialDialog,
  RescheduleDialog,
  CancelAssessmentDialog,
  FinalInsuranceDialog,
  RestoreReferralDialog,
} from "@/components/referrals";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const referralStatuses = [
  { value: "all", label: "All Statuses" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Insurance Approved" },
  { value: "denied", label: "Insurance Denied" },
  { value: "md_order_sent", label: "MD Order Faxed" },
  { value: "md_notes_faxed", label: "MD Notes Faxed" },
  { value: "md_order_received", label: "MD Order Received" },
  { value: "md_notes_received", label: "MD Notes Received" },
  { value: "assessment_scheduled", label: "Assessment Scheduled" },
  { value: "assessment_completed", label: "Assessment Completed" },
  { value: "pcst_completed", label: "PCST Completed" },
  { value: "cms485_completed", label: "485 Completed" },
  { value: "cms485_faxed", label: "485 Faxed to MD" },
  { value: "cms485_received", label: "485 Received" },
  { value: "submitted_to_insurance", label: "Submitted to Insurance" },
  { value: "insurance_final_approval", label: "Insurance Final Approval" },
  { value: "insurance_final_denial", label: "Insurance Final Denial" },
  { value: "insurance_modifications", label: "Insurance Modifications" },
  { value: "admitted", label: "Transferred to Clients (Admitted)" },
  { value: "cold", label: "Cold Lead" },
  { value: "non_admit", label: "Non-Admit (Archived)" },
];

const statusConfig = {
  pending: { color: "bg-slate-100 text-slate-700 border-slate-200", icon: Clock },
  approved: { color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: CheckCircle },
  denied: { color: "bg-red-100 text-red-700 border-red-200", icon: XCircle },
  md_order_sent: { color: "bg-blue-100 text-blue-700 border-blue-200", icon: Send },
  md_notes_faxed: { color: "bg-blue-100 text-blue-700 border-blue-200", icon: Send },
  md_order_received: { color: "bg-indigo-100 text-indigo-700 border-indigo-200", icon: FileText },
  md_notes_received: { color: "bg-indigo-100 text-indigo-700 border-indigo-200", icon: FileText },
  assessment_scheduled: { color: "bg-amber-100 text-amber-700 border-amber-200", icon: Calendar },
  assessment_completed: { color: "bg-teal-100 text-teal-700 border-teal-200", icon: CheckCircle },
  pcst_completed: { color: "bg-purple-100 text-purple-700 border-purple-200", icon: CheckCircle },
  cms485_completed: { color: "bg-cyan-100 text-cyan-700 border-cyan-200", icon: FileText },
  cms485_faxed: { color: "bg-blue-100 text-blue-700 border-blue-200", icon: Send },
  cms485_received: { color: "bg-indigo-100 text-indigo-700 border-indigo-200", icon: FileText },
  submitted_to_insurance: { color: "bg-amber-100 text-amber-700 border-amber-200", icon: Send },
  insurance_final_approval: { color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: CheckCircle },
  insurance_final_denial: { color: "bg-red-100 text-red-700 border-red-200", icon: XCircle },
  insurance_modifications: { color: "bg-orange-100 text-orange-700 border-orange-200", icon: AlertCircle },
  admitted: { color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: CheckCircle },
  cold: { color: "bg-sky-100 text-sky-700 border-sky-200", icon: Snowflake },
  non_admit: { color: "bg-gray-100 text-gray-700 border-gray-300", icon: XCircle },
};

function DetailRow({ label, value }) {
  const hasValue = value !== null && value !== undefined && String(value).trim() !== "";
  return (
    <div className="flex flex-col">
      <span className="text-xs text-slate-500">{label}</span>
      <span className={`text-sm ${hasValue ? "text-slate-800" : "text-slate-400 italic"}`}>
        {hasValue ? String(value) : "Not provided"}
      </span>
    </div>
  );
}


export default function ReferralsPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [referrals, setReferrals] = useState([]);
  const [admittedCount, setAdmittedCount] = useState(0);
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [converting, setConverting] = useState(false);
  const [bulkConverting, setBulkConverting] = useState(false);
  const orphanCount = referrals.filter((r) => !r.client_id).length;
  const [diagnosisOpen, setDiagnosisOpen] = useState(false);
  const [diagnosisSearch, setDiagnosisSearch] = useState("");
  const [secondaryDiagnosisOpen, setSecondaryDiagnosisOpen] = useState(false);
  const [secondaryDiagnosisSearch, setSecondaryDiagnosisSearch] = useState("");
  const [denialDialogOpen, setDenialDialogOpen] = useState(false);
  const [denialReason, setDenialReason] = useState("");
  const [pendingDenialReferralId, setPendingDenialReferralId] = useState(null);
  
  // Reschedule/Cancel assessment state
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState("");
  const [rescheduleTime, setRescheduleTime] = useState("09:00");
  const [rescheduleReason, setRescheduleReason] = useState("");
  const [cancelAssessmentDialogOpen, setCancelAssessmentDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [selectedAssessRnId, setSelectedAssessRnId] = useState("");
  
  // New referral form - includes client info since referral creates the client
  const [newReferral, setNewReferral] = useState({
    // Client Info (referral creates a new potential client)
    first_name: "",
    last_name: "",
    dob: "",
    phone: "",
    email: "",
    height: "",
    weight: "",
    // Referral Info
    referral_source: "",
    referral_date: new Date().toISOString().split("T")[0],
    primary_diagnosis: "",
    secondary_diagnosis: "",
    care_reasons: "",
    notes: "",
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== "all") {
        params.append("status", statusFilter);
      }

      const [referralsRes, clientsRes, employeesRes] = await Promise.all([
        axios.get(`${API}/referrals?${params.toString()}`),
        axios.get(`${API}/clients`),
        axios.get(`${API}/employees?role=rn`),
      ]);

      // Filter out non_admit referrals from active list unless specifically viewing non_admit
      let filteredReferrals = referralsRes.data;
      if (statusFilter !== "non_admit") {
        filteredReferrals = referralsRes.data.filter(r => r.status !== "non_admit");
      }
      // Admitted referrals have been transferred to Clients — only show them under the "Transferred" filter
      setAdmittedCount(referralsRes.data.filter(r => r.status === "admitted").length);
      if (statusFilter !== "admitted") {
        filteredReferrals = filteredReferrals.filter(r => r.status !== "admitted");
      }

      // Sort referrals alphabetically by patient last name, then first name
      const clientMap = {};
      (clientsRes.data || []).forEach((c) => { clientMap[c.id] = c; });
      const patientLastName = (r) => {
        if (r.client_last_name) return r.client_last_name;
        if (r.client_name) {
          const parts = r.client_name.trim().split(" ");
          return parts.length > 1 ? parts.slice(-1)[0] : parts[0];
        }
        if (r.client_id && clientMap[r.client_id]) return clientMap[r.client_id].last_name || "";
        return "";
      };
      const patientFirstName = (r) => {
        if (r.client_first_name) return r.client_first_name;
        if (r.client_name) return r.client_name.trim().split(" ")[0] || "";
        if (r.client_id && clientMap[r.client_id]) return clientMap[r.client_id].first_name || "";
        return "";
      };
      filteredReferrals = [...filteredReferrals].sort((a, b) => {
        const aLast = patientLastName(a).toLowerCase();
        const bLast = patientLastName(b).toLowerCase();
        if (aLast !== bLast) return aLast.localeCompare(bLast);
        return patientFirstName(a).toLowerCase().localeCompare(patientFirstName(b).toLowerCase());
      });

      setReferrals(filteredReferrals);
      setClients(clientsRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      console.error("Failed to fetch referrals:", error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleStatusChange = (value) => {
    setStatusFilter(value);
    if (value === "all") {
      searchParams.delete("status");
    } else {
      searchParams.set("status", value);
    }
    setSearchParams(searchParams);
  };

  const handleConvertAllPending = async () => {
    if (!window.confirm("Convert all pending referrals to potential clients?")) return;
    setBulkConverting(true);
    try {
      const res = await axios.post(`${API}/referrals/convert-all-pending`);
      toast.success(res.data.message || "Bulk conversion complete");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk conversion failed");
    } finally {
      setBulkConverting(false);
    }
  };


  const handleConvertToClient = async (referralId) => {
    setConverting(true);
    try {
      const res = await axios.post(`${API}/referrals/${referralId}/convert-to-client`);
      toast.success("Referral converted to Potential Client");
      if (selectedReferral && selectedReferral.id === referralId) {
        setSelectedReferral({ ...selectedReferral, client_id: res.data.client_id });
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to convert referral");
    } finally {
      setConverting(false);
    }
  };


  const handleCreateReferral = async (e) => {
    e.preventDefault();

    if (!newReferral.first_name || !newReferral.last_name) {
      toast.error("Please enter client's first and last name");
      return;
    }

    setCreating(true);
    try {
      // Step 1: Create the potential client first
      const clientPayload = {
        first_name: newReferral.first_name,
        last_name: newReferral.last_name,
        dob: newReferral.dob || null,
        phone: newReferral.phone || null,
        email: newReferral.email || null,
        primary_diagnosis: newReferral.primary_diagnosis || null,
        secondary_diagnoses: newReferral.secondary_diagnosis ? [newReferral.secondary_diagnosis] : [],
        care_reasons: newReferral.care_reasons || null,
        status: "potential", // Always starts as potential
      };

      const clientRes = await axios.post(`${API}/clients`, clientPayload);
      const newClient = clientRes.data;

      // Step 2: Create the referral linked to this client
      const referralPayload = {
        client_id: newClient.id,
        referral_source: newReferral.referral_source,
        referral_date: newReferral.referral_date,
        height: newReferral.height || null,
        weight: newReferral.weight || null,
        notes: newReferral.notes,
      };

      await axios.post(`${API}/referrals`, referralPayload);

      toast.success("Referral created! New potential client added.");
      setDialogOpen(false);
      setNewReferral({
        first_name: "",
        last_name: "",
        dob: "",
        phone: "",
        email: "",
        height: "",
        weight: "",
        referral_source: "",
        referral_date: new Date().toISOString().split("T")[0],
        primary_diagnosis: "",
        secondary_diagnosis: "",
        care_reasons: "",
        notes: "",
      });
      fetchData();
    } catch (error) {
      console.error("Failed to create referral:", error);
      // Handle both string and array error formats
      const errorDetail = error.response?.data?.detail;
      let errorMessage = "Failed to create referral";
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail) && errorDetail.length > 0) {
        errorMessage = errorDetail.map(e => e.msg || e.message).join(', ');
      }
      toast.error(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const handleAdmit = async (referral) => {
    try {
      const res = await axios.post(`${API}/referrals/${referral.id}/admit`);
      const linked = Object.values(res.data.linked || {}).reduce((a, b) => a + b, 0);
      toast.success(`Client admitted — referral file transferred to Clients${linked ? ` (${linked} attachment${linked > 1 ? "s" : ""} linked)` : ""}`, {
        action: { label: "Open client", onClick: () => navigate(`/clients/${res.data.client_id}`) },
      });
      fetchData();
      setDetailsOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to admit client");
    }
  };

  const handleUpdateReferral = async (referralId, updates, clientUpdates = null) => {
    try {
      await axios.patch(`${API}/referrals/${referralId}`, updates);
      
      // If client updates needed (like changing status to active when admitted)
      if (clientUpdates && selectedReferral) {
        await axios.patch(`${API}/clients/${selectedReferral.client_id}`, clientUpdates);
      }
      
      toast.success("Referral updated!");
      fetchData();
      setDetailsOpen(false);
    } catch (error) {
      console.error("Failed to update referral:", error);
      toast.error("Failed to update referral");
    }
  };

  // Mark a referral as "cold" (interested but not moving forward) — keeps the
  // record so the pipeline can be revived later, without cluttering active work.
  const handleMarkReferralCold = async (referralId, e) => {
    if (e) e.stopPropagation();
    try {
      await axios.patch(`${API}/referrals/${referralId}`, {
        status: "cold",
        cold_at: new Date().toISOString(),
      });
      toast.success("Referral marked as cold");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark cold");
    }
  };

  // Warm up a cold referral — put it back in the pending pipeline in one click.
  const [sheetBusy, setSheetBusy] = useState(null);
  const handleReferralSheet = async (referral, mode) => {
    setSheetBusy(mode);
    try {
      const res = await axios.get(`${API}/referrals/${referral.id}/sheet.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      if (mode === "download") {
        const a = document.createElement("a");
        a.href = url;
        a.download = `Referral_${getClientName(referral).replace(/\s+/g, "_")}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success("Referral sheet downloaded");
      } else {
        const w = window.open(url, "_blank");
        if (!w) toast.error("Pop-up blocked — allow pop-ups to print");
        else w.addEventListener("load", () => { try { w.print(); } catch (_) { /* user prints manually */ } });
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error("Referral sheet failed:", error);
      toast.error("Could not generate the referral sheet");
    } finally {
      setSheetBusy(null);
    }
  };

  const handleReopenReferral = async (referralId, e) => {
    if (e) e.stopPropagation();
    try {
      await axios.patch(`${API}/referrals/${referralId}`, {
        status: "pending",
        reopened_at: new Date().toISOString(),
      });
      toast.success("Referral reopened — back in the pipeline");
      fetchData();
      setDetailsOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reopen referral");
    }
  };

  // Confirmation state for referral delete
  const [deleteReferralTarget, setDeleteReferralTarget] = useState(null);

  // Referral intake diagnostics
  const [intakeLogOpen, setIntakeLogOpen] = useState(false);
  const [intakeLogLoading, setIntakeLogLoading] = useState(false);
  const [intakeLogData, setIntakeLogData] = useState(null);
  const [intakeLogHours, setIntakeLogHours] = useState(24);
  const [intakeLogCode, setIntakeLogCode] = useState("");
  const [intakeExpandedId, setIntakeExpandedId] = useState(null);

  const fetchIntakeLog = async () => {
    setIntakeLogLoading(true);
    try {
      const params = { hours: intakeLogHours };
      if (intakeLogCode.trim()) params.agency_code = intakeLogCode.trim();
      const res = await axios.get(`${API}/admin/referral-intake-log`, { params });
      setIntakeLogData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Couldn't load intake log");
    } finally {
      setIntakeLogLoading(false);
    }
  };

  useEffect(() => {
    if (intakeLogOpen) {
      fetchIntakeLog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intakeLogOpen, intakeLogHours]);

  const confirmDeleteReferral = async () => {
    if (!deleteReferralTarget) return;
    try {
      await axios.delete(`${API}/referrals/${deleteReferralTarget.id}`);
      toast.success("Referral deleted");
      setDeleteReferralTarget(null);
      setDetailsOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete referral");
    }
  };


  // State for move to non-admit option
  const [moveToNonAdmit, setMoveToNonAdmit] = useState(true);

  const handleDenyInsurance = (referralId) => {
    setPendingDenialReferralId(referralId);
    setDenialReason("");
    setMoveToNonAdmit(true); // Default to checked
    setDenialDialogOpen(true);
  };

  // State for final insurance decision dialog
  const [finalInsuranceDialogOpen, setFinalInsuranceDialogOpen] = useState(false);
  const [finalInsuranceDecision, setFinalInsuranceDecision] = useState("");
  const [finalInsuranceNotes, setFinalInsuranceNotes] = useState("");
  const [pendingFinalInsuranceId, setPendingFinalInsuranceId] = useState(null);
  const [finalMoveToNonAdmit, setFinalMoveToNonAdmit] = useState(true);

  const handleFinalInsuranceDecision = (referralId, decision) => {
    setPendingFinalInsuranceId(referralId);
    setFinalInsuranceDecision(decision);
    setFinalInsuranceNotes("");
    setFinalMoveToNonAdmit(decision === "denied"); // Default to checked if denied
    setFinalInsuranceDialogOpen(true);
  };

  const submitFinalInsuranceDecision = async () => {
    if (!pendingFinalInsuranceId) return;
    
    try {
      const statusMap = {
        "approved": "insurance_final_approval",
        "denied": finalMoveToNonAdmit ? "non_admit" : "insurance_final_denial",
        "modifications": "insurance_modifications"
      };
      
      const updateData = {
        insurance_final_status: finalInsuranceDecision,
        insurance_final_notes: finalInsuranceNotes || "",
        insurance_final_date: new Date().toISOString().split("T")[0],
        status: statusMap[finalInsuranceDecision]
      };
      
      // If moving to non-admit and no client exists, create one
      let clientId = selectedReferral?.client_id;
      
      if (finalInsuranceDecision === "denied" && finalMoveToNonAdmit && selectedReferral && !selectedReferral.client_id) {
        try {
          // Create a new client from the referral data with non_admit status
          const clientData = {
            first_name: selectedReferral.client_first_name || selectedReferral.client_name?.split(" ")[0] || "Unknown",
            last_name: selectedReferral.client_last_name || selectedReferral.client_name?.split(" ").slice(1).join(" ") || "Client",
            status: "non_admit",
            referral_source: selectedReferral.referral_source,
            notes: `Created from non-admit referral. Final insurance denial: ${finalInsuranceNotes || "No notes provided"}`,
          };
          
          const clientRes = await axios.post(`${API}/clients`, clientData);
          clientId = clientRes.data.id;
          
          // Link the referral to the new client
          updateData.client_id = clientId;
        } catch (clientError) {
          console.warn("Could not create client from referral:", clientError);
        }
      }
      
      await axios.patch(`${API}/referrals/${pendingFinalInsuranceId}`, updateData);
      
      // Update client if denied and moving to non-admit (for existing clients)
      if (finalInsuranceDecision === "denied" && finalMoveToNonAdmit && selectedReferral?.client_id) {
        try {
          await axios.patch(`${API}/clients/${selectedReferral.client_id}`, { status: "non_admit" });
        } catch (clientError) {
          console.warn("Could not update client status:", clientError);
        }
      }
      
      const message = finalInsuranceDecision === "denied" && finalMoveToNonAdmit 
        ? "Insurance denied - Moved to Non-Admit" 
        : `Insurance ${finalInsuranceDecision}`;
      toast.success(message);
      setFinalInsuranceDialogOpen(false);
      setPendingFinalInsuranceId(null);
      setFinalInsuranceNotes("");
      setFinalMoveToNonAdmit(true);
      fetchData();
      setDetailsOpen(false);
    } catch (error) {
      console.error("Failed to update insurance decision:", error);
      toast.error("Failed to update referral");
    }
  };

  const submitDenial = async () => {
    if (!pendingDenialReferralId) return;
    
    try {
      // Update referral status
      const updateData = {
        approval_status: "denied",
        denial_reason: denialReason || "No reason provided"
      };
      
      // If moving to non-admit, update the status to non_admit
      if (moveToNonAdmit) {
        updateData.status = "non_admit";
      } else {
        updateData.status = "denied";
      }
      
      // If moving to non-admit and no client exists, create one
      let clientId = selectedReferral?.client_id;
      
      if (moveToNonAdmit && selectedReferral && !selectedReferral.client_id) {
        try {
          // Create a new client from the referral data with non_admit status
          const clientData = {
            first_name: selectedReferral.client_first_name || selectedReferral.client_name?.split(" ")[0] || "Unknown",
            last_name: selectedReferral.client_last_name || selectedReferral.client_name?.split(" ").slice(1).join(" ") || "Client",
            status: "non_admit",
            referral_source: selectedReferral.referral_source,
            notes: `Created from non-admit referral. Denial reason: ${denialReason || "No reason provided"}`,
          };
          
          const clientRes = await axios.post(`${API}/clients`, clientData);
          clientId = clientRes.data.id;
          
          // Link the referral to the new client
          updateData.client_id = clientId;
        } catch (clientError) {
          console.warn("Could not create client from referral:", clientError);
        }
      }
      
      await axios.patch(`${API}/referrals/${pendingDenialReferralId}`, updateData);
      
      // Also update client to non_admit if client exists and option is selected
      if (moveToNonAdmit && clientId && selectedReferral?.client_id) {
        try {
          await axios.patch(`${API}/clients/${clientId}`, { status: "non_admit" });
        } catch (clientError) {
          // Client might not exist or already be deleted, ignore this error
          console.warn("Could not update client status:", clientError);
        }
      }
      
      toast.success(moveToNonAdmit ? "Insurance denied - Moved to Non-Admit" : "Insurance denied");
      setDenialDialogOpen(false);
      setPendingDenialReferralId(null);
      setDenialReason("");
      setMoveToNonAdmit(true);
      fetchData();
      setDetailsOpen(false);
    } catch (error) {
      console.error("Failed to deny insurance:", error);
      toast.error("Failed to update referral");
    }
  };

  const handleRescheduleAssessment = async () => {
    if (!selectedReferral || !rescheduleDate) {
      toast.error("Please select a new date");
      return;
    }
    
    try {
      await axios.patch(`${API}/referrals/${selectedReferral.id}`, {
        assessment_scheduled_date: rescheduleDate,
        assessment_scheduled_time: rescheduleTime,
        reschedule_count: (selectedReferral.reschedule_count || 0) + 1,
        reschedule_reason: rescheduleReason || null,
      });
      
      toast.success("Assessment rescheduled");
      setRescheduleDialogOpen(false);
      setRescheduleDate("");
      setRescheduleTime("09:00");
      setRescheduleReason("");
      fetchData();
      setDetailsOpen(false);
    } catch (error) {
      console.error("Failed to reschedule:", error);
      toast.error("Failed to reschedule assessment");
    }
  };

  const handleCancelAssessment = async () => {
    if (!selectedReferral) return;
    
    try {
      await axios.patch(`${API}/referrals/${selectedReferral.id}`, {
        assessment_scheduled_date: null,
        assessment_scheduled_time: null,
        status: "md_notes_received", // Go back to previous status
        reschedule_reason: cancelReason || "Assessment cancelled",
      });
      
      toast.success("Assessment cancelled");
      setCancelAssessmentDialogOpen(false);
      setCancelReason("");
      fetchData();
      setDetailsOpen(false);
    } catch (error) {
      console.error("Failed to cancel assessment:", error);
      toast.error("Failed to cancel assessment");
    }
  };

  // Restore Non-Admit referral back to active
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [_restoreStatus, setRestoreStatus] = useState("pending");
  const [restoringReferral, setRestoringReferral] = useState(false);

  const handleRestoreReferral = async () => {
    if (!selectedReferral) return;
    
    setRestoringReferral(true);
    try {
      // When restoring from non-admit, reset all workflow progress
      // This brings the referral back to the beginning (pending status)
      // so they can go through the approval process again
      const resetData = {
        status: "pending",
        approval_status: null,
        denial_reason: null,
        // Reset all workflow flags and dates
        md_order_faxed: false,
        md_order_faxed_date: null,
        md_notes_faxed: false,
        md_notes_faxed_date: null,
        md_order_received: false,
        md_order_received_date: null,
        md_notes_received: false,
        md_notes_received_date: null,
        assessment_scheduled_date: null,
        assessment_scheduled_time: null,
        assessment_completed: false,
        assessment_completed_date: null,
        pcst_completed: false,
        pcst_completed_date: null,
        cms485_completed: false,
        cms485_completed_date: null,
        cms485_faxed: false,
        cms485_faxed_date: null,
        cms485_received: false,
        cms485_received_date: null,
        submitted_to_insurance: false,
        submitted_to_insurance_date: null,
        insurance_final_status: null,
        insurance_final_notes: null,
        insurance_final_date: null,
      };
      
      await axios.patch(`${API}/referrals/${selectedReferral.id}`, resetData);
      
      // Also update client status back to active if exists
      if (selectedReferral.client_id) {
        try {
          await axios.patch(`${API}/clients/${selectedReferral.client_id}`, { 
            status: "active" 
          });
        } catch (clientError) {
          console.warn("Could not update client status:", clientError);
        }
      }
      
      toast.success("Referral restored - workflow reset to beginning");
      setRestoreDialogOpen(false);
      setRestoreStatus("pending");
      fetchData();
      setDetailsOpen(false);
    } catch (error) {
      console.error("Failed to restore referral:", error);
      toast.error("Failed to restore referral");
    } finally {
      setRestoringReferral(false);
    }
  };

  const getClientName = (referral) => {
    // For online form referrals, use the stored client_name or construct from first/last name
    if (referral.client_name) {
      return referral.client_name;
    }
    // For online referrals with separate first/last name fields
    if (referral.client_first_name && referral.client_last_name) {
      return `${referral.client_first_name} ${referral.client_last_name}`;
    }
    // For internal referrals, look up the client
    if (referral.client_id) {
      const client = clients.find((c) => c.id === referral.client_id);
      return client ? `${client.first_name} ${client.last_name}` : "Unknown Client";
    }
    return "Unknown Client";
  };

  const formatStatus = (status) => {
    return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const getWorkflowSteps = (referral) => {
    // Helper to determine completed status based on workflow position
    const isStepReached = (checkStatus) => {
      const statusOrder = [
        "pending", "approved", "md_order_sent", "md_notes_faxed", 
        "md_order_received", "md_notes_received",
        "assessment_scheduled", "assessment_completed", "pcst_completed",
        "cms485_completed", "cms485_faxed", "cms485_received",
        "submitted_to_insurance", "insurance_final_approval", "admitted"
      ];
      const currentIndex = statusOrder.indexOf(referral.status);
      const checkIndex = statusOrder.indexOf(checkStatus);
      return currentIndex >= checkIndex;
    };

    return [
      {
        label: "Referral Received",
        description: "New referral intake",
        completed: true,
        date: referral.referral_date,
      },
      {
        label: referral.approval_status === "denied" ? "Insurance Denied" : "Insurance Accepted",
        description: referral.approval_status === "denied" 
          ? (referral.denial_reason || "Coverage not approved") 
          : "Coverage verified",
        completed: referral.approval_status === "approved",
        denied: referral.approval_status === "denied",
        action: !referral.approval_status ? () => handleUpdateReferral(referral.id, {
          approval_status: "approved",
          status: "approved",
        }) : null,
        actionLabel: "Approve",
        denyAction: !referral.approval_status ? () => handleDenyInsurance(referral.id) : null,
        denyLabel: "Deny",
      },
      {
        label: "MD Order Faxed",
        description: "Order request sent to physician",
        completed: referral.md_order_faxed,
        date: referral.md_order_faxed_date,
        action: referral.approval_status === "approved" && !referral.md_order_faxed ? () => handleUpdateReferral(referral.id, {
          md_order_faxed: true,
          md_order_faxed_date: new Date().toISOString().split("T")[0],
          status: "md_order_sent",
        }) : null,
        actionLabel: "Mark Faxed",
      },
      {
        label: "MD Notes Faxed",
        description: "Medical notes sent to physician",
        completed: referral.md_notes_faxed,
        date: referral.md_notes_faxed_date,
        action: referral.md_order_faxed && !referral.md_notes_faxed ? () => handleUpdateReferral(referral.id, {
          md_notes_faxed: true,
          md_notes_faxed_date: new Date().toISOString().split("T")[0],
          status: "md_notes_faxed",
        }) : null,
        actionLabel: "Mark Notes Faxed",
      },
      {
        label: "MD Order Received",
        description: "Physician order obtained",
        completed: referral.md_order_received,
        date: referral.md_order_received_date,
        action: referral.md_order_faxed && !referral.md_order_received ? () => handleUpdateReferral(referral.id, {
          md_order_received: true,
          md_order_received_date: new Date().toISOString().split("T")[0],
          status: "md_order_received",
        }) : null,
        actionLabel: "Mark Order Received",
      },
      {
        label: "MD Notes Received",
        description: "Medical notes returned from physician",
        completed: referral.md_notes_received,
        date: referral.md_notes_received_date,
        action: referral.md_notes_faxed && !referral.md_notes_received ? () => handleUpdateReferral(referral.id, {
          md_notes_received: true,
          md_notes_received_date: new Date().toISOString().split("T")[0],
          status: "md_notes_received",
        }) : null,
        actionLabel: "Mark Notes Received",
      },
      {
        label: "RN Assessment Scheduled",
        description: "Home visit scheduled",
        completed: isStepReached("assessment_scheduled") || referral.assessment_scheduled_date,
        date: referral.assessment_scheduled_date,
        requiresRN: true, // RN only can schedule
      },
      {
        label: "Assessment Completed",
        description: "RN evaluation done",
        completed: referral.assessment_completed,
        date: referral.assessment_completed_date,
        action: referral.assessment_scheduled_date && !referral.assessment_completed ? () => handleUpdateReferral(referral.id, {
          assessment_completed: true,
          assessment_completed_date: new Date().toISOString().split("T")[0],
          status: "assessment_completed",
        }) : null,
        actionLabel: "Complete Assessment",
      },
      {
        label: "PCST Completed",
        description: "Personal Care Screening Tool",
        completed: referral.pcst_completed,
        date: referral.pcst_completed_date,
        action: referral.assessment_completed && !referral.pcst_completed ? () => handleUpdateReferral(referral.id, {
          pcst_completed: true,
          pcst_completed_date: new Date().toISOString().split("T")[0],
          status: "pcst_completed",
        }) : null,
        actionLabel: "Mark PCST Complete",
      },
      {
        label: "485 Completed",
        description: "CMS 485 form completed",
        completed: referral.cms485_completed,
        date: referral.cms485_completed_date,
        action: referral.pcst_completed && !referral.cms485_completed ? () => handleUpdateReferral(referral.id, {
          cms485_completed: true,
          cms485_completed_date: new Date().toISOString().split("T")[0],
          status: "cms485_completed",
        }) : null,
        actionLabel: "Mark 485 Complete",
      },
      {
        label: "485 Faxed to MD",
        description: "Sent for physician signature",
        completed: referral.cms485_faxed,
        date: referral.cms485_faxed_date,
        action: referral.cms485_completed && !referral.cms485_faxed ? () => handleUpdateReferral(referral.id, {
          cms485_faxed: true,
          cms485_faxed_date: new Date().toISOString().split("T")[0],
          status: "cms485_faxed",
        }) : null,
        actionLabel: "Mark Faxed",
      },
      {
        label: "Received 485",
        description: "MD signed and returned",
        completed: referral.cms485_received,
        date: referral.cms485_received_date,
        action: referral.cms485_faxed && !referral.cms485_received ? () => handleUpdateReferral(referral.id, {
          cms485_received: true,
          cms485_received_date: new Date().toISOString().split("T")[0],
          status: "cms485_received",
        }) : null,
        actionLabel: "Mark Received",
      },
      {
        label: "Submitted to Insurance",
        description: "485 sent for payment approval",
        completed: referral.submitted_to_insurance,
        date: referral.submitted_to_insurance_date,
        action: referral.cms485_received && !referral.submitted_to_insurance ? () => handleUpdateReferral(referral.id, {
          submitted_to_insurance: true,
          submitted_to_insurance_date: new Date().toISOString().split("T")[0],
          status: "submitted_to_insurance",
        }) : null,
        actionLabel: "Mark Submitted",
      },
      {
        label: referral.insurance_final_status === "denied" 
          ? "Insurance Denied" 
          : referral.insurance_final_status === "modifications"
          ? "Insurance Modifications"
          : "Insurance Approval",
        description: referral.insurance_final_status === "denied"
          ? (referral.insurance_final_notes || "Payment denied")
          : referral.insurance_final_status === "modifications"
          ? (referral.insurance_final_notes || "Modifications required")
          : "Payment approved for services",
        completed: referral.insurance_final_status === "approved",
        denied: referral.insurance_final_status === "denied",
        modifications: referral.insurance_final_status === "modifications",
        date: referral.insurance_final_date,
        action: referral.submitted_to_insurance && !referral.insurance_final_status ? () => handleUpdateReferral(referral.id, {
          insurance_final_status: "approved",
          insurance_final_date: new Date().toISOString().split("T")[0],
          status: "insurance_final_approval",
        }) : null,
        actionLabel: "Approve",
        denyAction: referral.submitted_to_insurance && !referral.insurance_final_status ? () => handleFinalInsuranceDecision(referral.id, "denied") : null,
        denyLabel: "Deny",
        modifyAction: referral.submitted_to_insurance && !referral.insurance_final_status ? () => handleFinalInsuranceDecision(referral.id, "modifications") : null,
        modifyLabel: "Modifications",
      },
      {
        label: "Client Admitted",
        description: "Care services started",
        completed: referral.status === "admitted",
        action: referral.insurance_final_status === "approved" && referral.status !== "admitted" ? () => handleAdmit(referral) : null,
        actionLabel: "Admit to Service",
      },
    ];
  };

  return (
    <Layout>
      {/* Page Header */}
      <div className="page-header">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="page-title">Referrals</h1>
            <p className="page-subtitle">
              Receive new referrals and track intake workflow.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => setIntakeLogOpen(true)}
              data-testid="open-intake-log-btn"
              title="Audit public-form referral submissions (successes and silent failures)"
            >
              <FileText className="w-4 h-4" />
              Intake Diagnostics
            </Button>
            {orphanCount > 0 && (
              <Button
                variant="outline"
                className="gap-2 text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                onClick={handleConvertAllPending}
                disabled={bulkConverting}
                data-testid="convert-all-pending-btn"
              >
                {bulkConverting ? "Converting..." : `Convert All Pending (${orphanCount})`}
              </Button>
            )}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="new-referral-btn">
                <Plus className="w-4 h-4" />
                New Referral
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>New Referral Intake</DialogTitle>
                <DialogDescription>
                  Enter the referred client&apos;s information. This will create a new potential client.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateReferral}>
                <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                  {/* Client Information Section */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-slate-700 border-b pb-2">Client Information</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="first_name">First Name *</Label>
                        <Input
                          id="first_name"
                          placeholder="John"
                          value={newReferral.first_name}
                          onChange={(e) =>
                            setNewReferral((prev) => ({ ...prev, first_name: e.target.value }))
                          }
                          required
                          data-testid="ref-first-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="last_name">Last Name *</Label>
                        <Input
                          id="last_name"
                          placeholder="Doe"
                          value={newReferral.last_name}
                          onChange={(e) =>
                            setNewReferral((prev) => ({ ...prev, last_name: e.target.value }))
                          }
                          required
                          data-testid="ref-last-name"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="dob">Date of Birth</Label>
                        <Input
                          id="dob"
                          type="date"
                          value={newReferral.dob}
                          onChange={(e) =>
                            setNewReferral((prev) => ({ ...prev, dob: e.target.value }))
                          }
                          data-testid="ref-dob"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone</Label>
                        <Input
                          id="phone"
                          type="tel"
                          placeholder="(555) 123-4567"
                          value={newReferral.phone}
                          onChange={(e) =>
                            setNewReferral((prev) => ({ ...prev, phone: e.target.value }))
                          }
                          data-testid="ref-phone"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="client-email">Client Email</Label>
                      <Input
                        id="client-email"
                        type="email"
                        placeholder="client@example.com"
                        value={newReferral.email}
                        onChange={(e) =>
                          setNewReferral((prev) => ({ ...prev, email: e.target.value }))
                        }
                        data-testid="ref-email"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="height">Height</Label>
                        <Input
                          id="height"
                          placeholder="5'8&quot;"
                          value={newReferral.height}
                          onChange={(e) =>
                            setNewReferral((prev) => ({ ...prev, height: e.target.value }))
                          }
                          data-testid="ref-height"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="weight">Weight</Label>
                        <Input
                          id="weight"
                          placeholder="165 lbs"
                          value={newReferral.weight}
                          onChange={(e) =>
                            setNewReferral((prev) => ({ ...prev, weight: e.target.value }))
                          }
                          data-testid="ref-weight"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Primary Diagnosis (ICD-10)</Label>
                      <Popover open={diagnosisOpen} onOpenChange={setDiagnosisOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={diagnosisOpen}
                            className="w-full justify-between font-normal"
                            data-testid="ref-diagnosis"
                          >
                            {newReferral.primary_diagnosis
                              ? newReferral.primary_diagnosis
                              : "Search ICD-10 code or description..."}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[400px] p-0" align="start">
                          <Command>
                            <CommandInput 
                              placeholder="Search ICD-10 codes..." 
                              value={diagnosisSearch}
                              onValueChange={setDiagnosisSearch}
                            />
                            <CommandList>
                              <CommandEmpty>No diagnosis found.</CommandEmpty>
                              <CommandGroup className="max-h-64 overflow-y-auto">
                                {searchICD10(diagnosisSearch).map((item) => (
                                  <CommandItem
                                    key={item.code}
                                    value={`${item.code} ${item.description}`}
                                    onSelect={() => {
                                      setNewReferral((prev) => ({
                                        ...prev,
                                        primary_diagnosis: `${item.code} - ${item.description}`,
                                      }));
                                      setDiagnosisOpen(false);
                                      setDiagnosisSearch("");
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        newReferral.primary_diagnosis?.includes(item.code)
                                          ? "opacity-100"
                                          : "opacity-0"
                                      )}
                                    />
                                    <span className="font-mono text-xs mr-2">{item.code}</span>
                                    <span className="text-sm">{item.description}</span>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="space-y-2">
                      <Label>Secondary Diagnosis (ICD-10)</Label>
                      <Popover open={secondaryDiagnosisOpen} onOpenChange={setSecondaryDiagnosisOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={secondaryDiagnosisOpen}
                            className="w-full justify-between font-normal"
                            data-testid="ref-secondary-diagnosis"
                          >
                            {newReferral.secondary_diagnosis
                              ? newReferral.secondary_diagnosis
                              : "Search ICD-10 code (optional)..."}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[400px] p-0" align="start">
                          <Command>
                            <CommandInput 
                              placeholder="Search ICD-10 codes..." 
                              value={secondaryDiagnosisSearch}
                              onValueChange={setSecondaryDiagnosisSearch}
                            />
                            <CommandList>
                              <CommandEmpty>No diagnosis found.</CommandEmpty>
                              <CommandGroup className="max-h-64 overflow-y-auto">
                                {searchICD10(secondaryDiagnosisSearch).map((item) => (
                                  <CommandItem
                                    key={item.code}
                                    value={`${item.code} ${item.description}`}
                                    onSelect={() => {
                                      setNewReferral((prev) => ({
                                        ...prev,
                                        secondary_diagnosis: `${item.code} - ${item.description}`,
                                      }));
                                      setSecondaryDiagnosisOpen(false);
                                      setSecondaryDiagnosisSearch("");
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        newReferral.secondary_diagnosis?.includes(item.code)
                                          ? "opacity-100"
                                          : "opacity-0"
                                      )}
                                    />
                                    <span className="font-mono text-xs mr-2">{item.code}</span>
                                    <span className="text-sm">{item.description}</span>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="care_reasons">Reason for Home Care</Label>
                      <Textarea
                        id="care_reasons"
                        placeholder="Why is this client being referred?"
                        value={newReferral.care_reasons}
                        onChange={(e) =>
                          setNewReferral((prev) => ({ ...prev, care_reasons: e.target.value }))
                        }
                        rows={2}
                        data-testid="ref-care-reasons"
                      />
                    </div>
                  </div>

                  {/* Referral Information Section */}
                  <div className="space-y-4 pt-2">
                    <h4 className="text-sm font-semibold text-slate-700 border-b pb-2">Referral Details</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="source">Referral Source</Label>
                        <Input
                          id="source"
                          placeholder="Hospital, Doctor, Family"
                          value={newReferral.referral_source}
                          onChange={(e) =>
                            setNewReferral((prev) => ({
                              ...prev,
                              referral_source: e.target.value,
                            }))
                          }
                          data-testid="ref-source-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="date">Referral Date</Label>
                        <Input
                          id="date"
                          type="date"
                          value={newReferral.referral_date}
                          onChange={(e) =>
                            setNewReferral((prev) => ({
                              ...prev,
                              referral_date: e.target.value,
                            }))
                          }
                          data-testid="ref-date-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="notes">Additional Notes</Label>
                      <Textarea
                        id="notes"
                        placeholder="Any other relevant information..."
                        value={newReferral.notes}
                        onChange={(e) =>
                          setNewReferral((prev) => ({ ...prev, notes: e.target.value }))
                        }
                        rows={2}
                        data-testid="ref-notes-input"
                      />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={creating} data-testid="create-ref-btn">
                    {creating ? "Creating..." : "Create Referral"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <Select value={statusFilter} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-full md:w-56" data-testid="ref-status-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              {referralStatuses.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Referral Pipeline */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {referrals.filter((r) => r.status === "pending").length}
              </p>
              <p className="text-sm text-slate-500">Pending</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Send className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {referrals.filter((r) => ["md_order_sent", "md_order_received", "approved"].includes(r.status)).length}
              </p>
              <p className="text-sm text-slate-500">In Progress</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {referrals.filter((r) => ["assessment_scheduled", "assessment_completed"].includes(r.status)).length}
              </p>
              <p className="text-sm text-slate-500">Assessment</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900" data-testid="pipeline-admitted-count">
                {admittedCount}
              </p>
              <button type="button" onClick={() => handleStatusChange("admitted")} className="text-sm text-slate-500 hover:text-emerald-700 hover:underline" data-testid="pipeline-admitted-link">Admitted · transferred to Clients</button>
            </div>
          </div>
        </div>
      </div>

      {/* Referral List */}
      <div className="bg-white rounded-xl border border-slate-200">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="spinner w-8 h-8"></div>
          </div>
        ) : referrals.length === 0 ? (
          <div className="empty-state py-16">
            <div className="empty-state-icon">
              <FileText className="w-8 h-8 text-slate-400" />
            </div>
            <p className="empty-state-title">No referrals yet</p>
            <p className="empty-state-description">
              Click &quot;New Referral&quot; to intake a new client referral.
            </p>
            <Button
              size="sm"
              onClick={() => setDialogOpen(true)}
              className="mt-4"
              data-testid="empty-new-referral-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Referral
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {referrals.map((referral) => {
              const StatusIcon = statusConfig[referral.status]?.icon || Clock;
              return (
                <div
                  key={referral.id}
                  className="p-4 hover:bg-slate-50 transition-colors cursor-pointer"
                  onClick={() => {
                    setSelectedReferral(referral);
                    setDetailsOpen(true);
                  }}
                  data-testid={`referral-row-${referral.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium">
                        {getClientName(referral)
                          .split(" ")
                          .map((n) => n[0])
                          .join("")}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">
                          {getClientName(referral)}
                        </p>
                        <p className="text-sm text-slate-500">
                          {referral.referral_source || "No source"} • {referral.referral_date || "Online submission"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`badge border ${
                          statusConfig[referral.status]?.color || "bg-slate-100"
                        }`}
                      >
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {formatStatus(referral.status)}
                      </span>
                      {referral.status !== "cold" && referral.status !== "admitted" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 text-sky-600 hover:text-sky-700 hover:bg-sky-50"
                          title="Mark as Cold Lead"
                          onClick={(e) => handleMarkReferralCold(referral.id, e)}
                          data-testid={`mark-cold-${referral.id}`}
                        >
                          <Snowflake className="w-4 h-4" />
                        </Button>
                      )}
                      {referral.status === "cold" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 gap-1 px-2 text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                          title="Reopen referral — move back to Pending"
                          onClick={(e) => handleReopenReferral(referral.id, e)}
                          data-testid={`reopen-referral-${referral.id}`}
                        >
                          <RotateCcw className="w-4 h-4" />
                          <span className="text-xs font-medium">Reopen</span>
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        title="Delete referral"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteReferralTarget(referral);
                        }}
                        data-testid={`delete-referral-${referral.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      <ArrowRight className="w-4 h-4 text-slate-300" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Referral Details Modal */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedReferral && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  {getClientName(selectedReferral)}
                </DialogTitle>
                <DialogDescription>
                  Referral from {selectedReferral.referral_source || "Online Form"} on {selectedReferral.referral_date || new Date(selectedReferral.submitted_at).toLocaleDateString()}
                </DialogDescription>
                <div className="flex items-center gap-2 pt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleReferralSheet(selectedReferral, "download")}
                    disabled={!!sheetBusy}
                    className="gap-1.5"
                    data-testid="referral-sheet-download-btn"
                  >
                    <Download className="w-4 h-4" />
                    {sheetBusy === "download" ? "Preparing…" : "Download PDF"}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleReferralSheet(selectedReferral, "print")}
                    disabled={!!sheetBusy}
                    className="gap-1.5"
                    data-testid="referral-sheet-print-btn"
                  >
                    <Printer className="w-4 h-4" />
                    {sheetBusy === "print" ? "Preparing…" : "Print"}
                  </Button>
                </div>
              </DialogHeader>
              <div className="py-4">
                {/* Cold Lead Reopen Banner */}
                {selectedReferral.status === "cold" && (
                  <div className="mb-4 p-4 bg-sky-50 border border-sky-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-sky-900 flex items-center gap-2">
                          <Snowflake className="w-4 h-4" />
                          Cold Lead
                        </p>
                        <p className="text-xs text-sky-700 mt-1">
                          This referral was set aside as a cold lead. Reopen it
                          to move it back to <em>Pending</em> and continue the
                          intake pipeline.
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                        onClick={() => handleReopenReferral(selectedReferral.id)}
                        data-testid="reopen-referral-from-modal-btn"
                      >
                        <RotateCcw className="w-4 h-4 mr-1" />
                        Reopen
                      </Button>
                    </div>
                  </div>
                )}

                {/* Non-Admit Restore Banner */}
                {selectedReferral.status === "non_admit" && (
                  <div className="mb-4 p-4 bg-gray-100 border border-gray-300 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                          <XCircle className="w-4 h-4" />
                          Archived - Non-Admit
                        </p>
                        <p className="text-xs text-gray-600 mt-1">
                          This referral was closed due to insurance denial.
                          {selectedReferral.denial_reason && (
                            <span className="block mt-1">
                              <strong>Reason:</strong> {selectedReferral.denial_reason}
                            </span>
                          )}
                        </p>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="border-primary text-primary hover:bg-primary hover:text-white"
                        onClick={() => setRestoreDialogOpen(true)}
                        data-testid="restore-referral-btn"
                      >
                        <RotateCcw className="w-4 h-4 mr-1" />
                        Restore to Referrals
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Quick Link to Client Profile - only show if client exists in system */}
                {selectedReferral.client_id ? (
                  <div className="mb-4 p-3 bg-slate-50 rounded-lg flex items-center justify-between">
                    <span className="text-sm text-slate-600">View full client profile</span>
                    <Link to={`/clients/${selectedReferral.client_id}`}>
                      <Button size="sm" variant="outline">
                        Open Profile
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <p className="text-sm font-medium text-amber-800">Online Form Submission</p>
                        <p className="text-xs text-amber-600 mt-1">
                          This referral was submitted via the public form. Convert it to a Potential Client to start tracking care.
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleConvertToClient(selectedReferral.id)}
                        disabled={converting}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                        data-testid="convert-referral-btn"
                      >
                        {converting ? (
                          <>Converting...</>
                        ) : (
                          <>Convert to Potential Client</>
                        )}
                      </Button>
                    </div>
                    {selectedReferral.referrer_name && (
                      <p className="text-xs text-slate-600 mt-2">
                        <strong>Referred by:</strong> {selectedReferral.referrer_name} 
                        {selectedReferral.referrer_phone && ` • ${selectedReferral.referrer_phone}`}
                      </p>
                    )}
                  </div>
                )}

                {/* Full submission details */}
                <div className="mb-4 p-4 border border-slate-200 rounded-lg bg-white" data-testid="referral-submission-details">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3">Submission Details</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    <DetailRow label="Full Name" value={`${selectedReferral.client_first_name || ""} ${selectedReferral.client_last_name || ""}`.trim()} />
                    <DetailRow label="Date of Birth" value={selectedReferral.date_of_birth} />
                    <DetailRow label="Phone" value={selectedReferral.phone} />
                    <DetailRow label="Email" value={selectedReferral.email} />
                    <DetailRow label="Address" value={selectedReferral.address} />
                    <DetailRow label="City / State / ZIP" value={[selectedReferral.city, selectedReferral.state, selectedReferral.zip_code].filter(Boolean).join(", ")} />
                    <DetailRow label="Insurance Type" value={selectedReferral.insurance_type} />
                    <DetailRow label="Insurance / Medicaid ID" value={selectedReferral.insurance_id} />
                    <DetailRow label="Services Needed" value={Array.isArray(selectedReferral.services_needed) ? selectedReferral.services_needed.join(", ") : selectedReferral.services_needed} />
                    <DetailRow label="Diagnosis" value={selectedReferral.diagnosis} />
                    <DetailRow label="Special Needs" value={selectedReferral.special_needs} />
                    <DetailRow label="Urgency" value={selectedReferral.urgency} />
                    <DetailRow label="Preferred Contact" value={selectedReferral.preferred_contact_method} />
                  </div>
                  {(selectedReferral.physician_name || selectedReferral.physician_phone) && (
                    <div className="mt-4 pt-3 border-t">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Primary Care Doctor</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                        <DetailRow label="Doctor's Name" value={selectedReferral.physician_name} />
                        <DetailRow label="Specialty" value={selectedReferral.physician_specialty} />
                        <DetailRow label="Phone" value={selectedReferral.physician_phone} />
                        <DetailRow label="Fax" value={selectedReferral.physician_fax} />
                        <DetailRow label="Office Address" value={selectedReferral.physician_address} />
                        <DetailRow label="NPI" value={selectedReferral.physician_npi} />
                      </div>
                    </div>
                  )}
                  {(selectedReferral.referrer_name || selectedReferral.referrer_organization || selectedReferral.referrer_email) && (
                    <div className="mt-4 pt-3 border-t">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Referral Source</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                        <DetailRow label="Referrer Name" value={selectedReferral.referrer_name} />
                        <DetailRow label="Organization" value={selectedReferral.referrer_organization} />
                        <DetailRow label="Referrer Phone" value={selectedReferral.referrer_phone} />
                        <DetailRow label="Referrer Email" value={selectedReferral.referrer_email} />
                        <DetailRow label="Relationship" value={selectedReferral.referrer_relationship} />
                      </div>
                    </div>
                  )}
                  {selectedReferral.notes && (
                    <div className="mt-4 pt-3 border-t">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Additional Notes</p>
                      <p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedReferral.notes}</p>
                    </div>
                  )}
                </div>

                {/* Workflow Steps */}
                <h4 className="text-sm font-semibold text-slate-700 mb-4">
                  Intake Workflow Progress
                </h4>
                <div className="space-y-3">
                  {getWorkflowSteps(selectedReferral).map((step, index) => (
                    <div
                      key={index}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        step.completed
                          ? "bg-emerald-50 border-emerald-200"
                          : step.denied
                          ? "bg-red-50 border-red-200"
                          : "bg-slate-50 border-slate-200"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-6 h-6 rounded-full flex items-center justify-center ${
                            step.completed
                              ? "bg-emerald-500 text-white"
                              : step.denied
                              ? "bg-red-500 text-white"
                              : "bg-slate-300 text-white"
                          }`}
                        >
                          {step.completed ? (
                            <CheckCircle className="w-4 h-4" />
                          ) : step.denied ? (
                            <XCircle className="w-4 h-4" />
                          ) : (
                            <span className="text-xs">{index + 1}</span>
                          )}
                        </div>
                        <div>
                          <p
                            className={`text-sm font-medium ${
                              step.completed ? "text-emerald-700" : step.denied ? "text-red-700" : "text-slate-600"
                            }`}
                          >
                            {step.label}
                          </p>
                          <p className="text-xs text-slate-500">
                            {step.date || step.description}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {step.action && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={step.action}
                            data-testid={`workflow-action-${index}`}
                          >
                            {step.actionLabel}
                          </Button>
                        )}
                        {step.denyAction && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={step.denyAction}
                            data-testid={`workflow-deny-${index}`}
                          >
                            {step.denyLabel}
                          </Button>
                        )}
                        {step.modifyAction && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                            onClick={step.modifyAction}
                            data-testid={`workflow-modify-${index}`}
                          >
                            {step.modifyLabel}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Schedule Assessment - RN Only */}
                {selectedReferral.md_notes_received &&
                  !selectedReferral.assessment_scheduled_date && (
                    <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <h4 className="text-sm font-semibold text-amber-800 mb-3">
                        Schedule RN Assessment
                      </h4>
                      {user?.role === "rn" || user?.role === "admin" ? (
                        <>
                          <div className="grid grid-cols-3 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="assess-date">Date</Label>
                              <Input
                                id="assess-date"
                                type="date"
                                data-testid="assess-date-input"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="assess-time">Time</Label>
                              <Input
                                id="assess-time"
                                type="time"
                                defaultValue="09:00"
                                data-testid="assess-time-input"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="assess-rn">Assign RN</Label>
                              {employees.filter(emp => emp.role === "rn").length === 0 ? (
                                <p className="text-sm text-amber-600">
                                  No RN employees found. Please add RN staff in the Employees page first.
                                </p>
                              ) : (
                                <Select value={selectedAssessRnId} onValueChange={setSelectedAssessRnId}>
                                  <SelectTrigger data-testid="assess-rn-select" id="assess-rn-select">
                                    <SelectValue placeholder="Select RN" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {employees.filter(emp => emp.role === "rn").map((emp) => (
                                      <SelectItem key={emp.id} value={emp.id}>
                                        {emp.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            className="mt-3"
                            onClick={async () => {
                              const dateInput = document.getElementById("assess-date");
                              const timeInput = document.getElementById("assess-time");
                              if (!dateInput?.value) {
                                toast.error("Please select a date");
                                return;
                              }
                              if (!selectedAssessRnId) {
                                toast.error("Please select an RN to assign");
                                return;
                              }
                              await handleUpdateReferral(selectedReferral.id, {
                                assessment_scheduled_date: dateInput.value,
                                assessment_scheduled_time: timeInput?.value || "09:00",
                                assigned_rn_id: selectedAssessRnId,
                                status: "assessment_scheduled",
                              });
                              setSelectedAssessRnId(""); // Reset after scheduling completes
                            }}
                            data-testid="schedule-assess-btn"
                          >
                            Schedule Assessment
                          </Button>
                        </>
                      ) : (
                        <div className="text-sm text-amber-700 flex items-center gap-2">
                          <AlertCircle className="w-4 h-4" />
                          Only RN users can schedule assessments. Contact your RN to schedule.
                        </div>
                      )}
                    </div>
                  )}

                {/* Scheduled Assessment - Show Reschedule/Cancel Options */}
                {selectedReferral.assessment_scheduled_date && !selectedReferral.assessment_completed && (
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-blue-800">
                        Assessment Scheduled
                      </h4>
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                        {selectedReferral.assessment_scheduled_date} {selectedReferral.assessment_scheduled_time && `at ${selectedReferral.assessment_scheduled_time}`}
                      </span>
                    </div>
                    {(user?.role === "rn" || user?.role === "admin") && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setRescheduleDialogOpen(true);
                            setRescheduleDate(selectedReferral.assessment_scheduled_date);
                            setRescheduleTime(selectedReferral.assessment_scheduled_time || "09:00");
                          }}
                          data-testid="reschedule-assess-btn"
                        >
                          Reschedule
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setCancelAssessmentDialogOpen(true)}
                          data-testid="cancel-assess-btn"
                        >
                          Cancel Assessment
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Notes */}
                {selectedReferral.notes && (
                  <div className="mt-6 pt-4 border-t border-slate-200">
                    <h4 className="text-sm font-semibold text-slate-700 mb-2">
                      Notes
                    </h4>
                    <p className="text-sm text-slate-600">{selectedReferral.notes}</p>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Extracted Dialog Components */}
      <RescheduleDialog
        open={rescheduleDialogOpen}
        onOpenChange={setRescheduleDialogOpen}
        rescheduleDate={rescheduleDate}
        setRescheduleDate={setRescheduleDate}
        rescheduleTime={rescheduleTime}
        setRescheduleTime={setRescheduleTime}
        rescheduleReason={rescheduleReason}
        setRescheduleReason={setRescheduleReason}
        onSubmit={handleRescheduleAssessment}
      />

      <CancelAssessmentDialog
        open={cancelAssessmentDialogOpen}
        onOpenChange={setCancelAssessmentDialogOpen}
        cancelReason={cancelReason}
        setCancelReason={setCancelReason}
        onSubmit={handleCancelAssessment}
      />

      <DenialDialog
        open={denialDialogOpen}
        onOpenChange={setDenialDialogOpen}
        denialReason={denialReason}
        setDenialReason={setDenialReason}
        moveToNonAdmit={moveToNonAdmit}
        setMoveToNonAdmit={setMoveToNonAdmit}
        onSubmit={submitDenial}
        onCancel={() => {
          setDenialDialogOpen(false);
          setPendingDenialReferralId(null);
        }}
      />

      <FinalInsuranceDialog
        open={finalInsuranceDialogOpen}
        onOpenChange={setFinalInsuranceDialogOpen}
        decision={finalInsuranceDecision}
        notes={finalInsuranceNotes}
        setNotes={setFinalInsuranceNotes}
        moveToNonAdmit={finalMoveToNonAdmit}
        setMoveToNonAdmit={setFinalMoveToNonAdmit}
        onSubmit={submitFinalInsuranceDecision}
        onCancel={() => {
          setFinalInsuranceDialogOpen(false);
          setPendingFinalInsuranceId(null);
        }}
      />

      <RestoreReferralDialog
        open={restoreDialogOpen}
        onOpenChange={setRestoreDialogOpen}
        selectedReferral={selectedReferral}
        restoring={restoringReferral}
        onConfirm={handleRestoreReferral}
      />

      {/* Referral Intake Diagnostics */}
      <Dialog open={intakeLogOpen} onOpenChange={setIntakeLogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="intake-log-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-teal-600" />
              Referral Intake Diagnostics
            </DialogTitle>
            <DialogDescription>
              Audit every public form submission — including the silent
              failures. Handy for diagnosing typoed agency codes and confirming
              that submissions actually landed.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap items-end gap-3 pb-3 border-b">
            <div className="space-y-1">
              <Label htmlFor="intake-hours" className="text-xs">Window</Label>
              <select
                id="intake-hours"
                value={intakeLogHours}
                onChange={(e) => setIntakeLogHours(Number(e.target.value))}
                className="h-9 px-3 rounded-md border border-slate-200 bg-white text-sm"
                data-testid="intake-hours-select"
              >
                <option value={24}>Last 24 hours</option>
                <option value={72}>Last 3 days</option>
                <option value={168}>Last 7 days</option>
                <option value={720}>Last 30 days</option>
              </select>
            </div>
            <div className="flex-1 min-w-[220px] space-y-1">
              <Label htmlFor="intake-code" className="text-xs">
                Agency code (e.g. <code className="text-teal-700">apexwi</code>) — leave blank for your own
              </Label>
              <Input
                id="intake-code"
                placeholder="e.g. apexwi"
                value={intakeLogCode}
                onChange={(e) => setIntakeLogCode(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") fetchIntakeLog(); }}
                data-testid="intake-code-input"
              />
            </div>
            <Button
              onClick={fetchIntakeLog}
              disabled={intakeLogLoading}
              data-testid="intake-refresh-btn"
            >
              {intakeLogLoading ? "Loading…" : "Refresh"}
            </Button>
          </div>

          {intakeLogData && (
            <div className="space-y-3 py-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="rounded-lg bg-slate-50 p-3 border border-slate-200">
                  <p className="text-xs text-slate-500">Attempts</p>
                  <p className="text-2xl font-semibold">{intakeLogData.summary.total_attempts}</p>
                </div>
                <div className="rounded-lg bg-emerald-50 p-3 border border-emerald-200">
                  <p className="text-xs text-emerald-700">Success</p>
                  <p className="text-2xl font-semibold text-emerald-800">{intakeLogData.summary.success}</p>
                </div>
                <div className="rounded-lg bg-amber-50 p-3 border border-amber-200">
                  <p className="text-xs text-amber-700">Agency Not Found</p>
                  <p className="text-2xl font-semibold text-amber-800">{intakeLogData.summary.agency_not_found}</p>
                </div>
                <div className="rounded-lg bg-red-50 p-3 border border-red-200">
                  <p className="text-xs text-red-700">Server Error</p>
                  <p className="text-2xl font-semibold text-red-800">{intakeLogData.summary.server_error}</p>
                </div>
              </div>

              {intakeLogData.summary.agency_not_found > 0 && (
                <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
                  <span className="font-medium">Heads up:</span>{" "}
                  {intakeLogData.summary.agency_not_found} submission(s) hit a
                  code that doesn't exist in your database. Either the public
                  form's URL was pointed at the wrong agency code, or the
                  agency hasn't been created yet.
                </div>
              )}

              <div className="rounded-lg border divide-y max-h-[52vh] overflow-y-auto">
                {intakeLogData.attempts.length === 0 && (
                  <div className="p-8 text-center text-sm text-slate-500">
                    No submissions in this window.
                  </div>
                )}
                {intakeLogData.attempts.map((a) => (
                  <div key={a.id} className="p-3 text-sm">
                    <button
                      className="w-full text-left flex items-start justify-between gap-3"
                      onClick={() => setIntakeExpandedId(intakeExpandedId === a.id ? null : a.id)}
                      data-testid={`intake-row-${a.id}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              a.status === "success"
                                ? "bg-emerald-100 text-emerald-700"
                                : a.status === "agency_not_found"
                                ? "bg-amber-100 text-amber-700"
                                : "bg-red-100 text-red-700"
                            }`}
                          >
                            {a.status?.replace("_", " ")}
                          </span>
                          <span className="font-medium text-slate-900">
                            {a.client_name || "(no client name)"}
                          </span>
                          <span className="text-xs text-slate-400">→ {a.agency_code || "?"}</span>
                        </div>
                        <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-3">
                          <span>{new Date(a.submitted_at).toLocaleString()}</span>
                          {a.client_phone && <span>📞 {a.client_phone}</span>}
                          {a.client_email && <span>✉ {a.client_email}</span>}
                          {a.referrer_name && <span>Referrer: {a.referrer_name}</span>}
                        </div>
                        {a.error && (
                          <p className="text-xs text-red-700 mt-1">{a.error}</p>
                        )}
                      </div>
                      <ChevronsUpDown className="w-4 h-4 text-slate-400 mt-1" />
                    </button>
                    {intakeExpandedId === a.id && (
                      <pre className="mt-2 p-2 bg-slate-50 rounded-md text-xs text-slate-700 overflow-x-auto max-h-56">
                        {JSON.stringify(a.raw_payload, null, 2)}
                      </pre>
                    )}
                  </div>
                ))}
              </div>

              <p className="text-xs text-slate-500">
                Showing since{" "}
                <code>{new Date(intakeLogData.since).toLocaleString()}</code>{" "}
                · {intakeLogData.referrals_in_db} referral(s) actually saved
                to the pipeline in this window.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIntakeLogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete referral confirmation */}
      <Dialog
        open={!!deleteReferralTarget}
        onOpenChange={(o) => { if (!o) setDeleteReferralTarget(null); }}
      >
        <DialogContent className="sm:max-w-md" data-testid="delete-referral-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete referral?
            </DialogTitle>
            <DialogDescription>
              This will permanently remove{" "}
              <span className="font-medium text-slate-900">
                {deleteReferralTarget && getClientName(deleteReferralTarget)}
              </span>{" "}
              from your referrals list. This cannot be undone. If you're not
              sure, mark them as <em>Cold Lead</em> instead.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteReferralTarget(null)}
            >
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={confirmDeleteReferral}
              data-testid="delete-referral-confirm-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
